import { collection, doc, getDoc, getDocs, getFirestore, writeBatch } from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { storage } from "../../../firebase/firebaseConfig";

type AllClientsMetadata = {
  ultimaRenovacionAt?: Date;
  ultimoCreditoUid?: string;
};

const formatearDosDigitos = (numero) => {
  return numero < 10 ? `0${numero}` : numero;
};

const formatearFechaInicial = (fechaInicialState) => {
  const fecha = new Date(fechaInicialState);
  fecha.setDate(fecha.getDate() + 1);

  const dia = fecha.getDate();
  const mes = fecha.getMonth() + 1;
  const anio = fecha.getFullYear();

  return `${formatearDosDigitos(dia)}/${formatearDosDigitos(mes)}/${anio}`;
};

const crearAllClientsCollectionRef = (db, usuarioRuta) =>
  collection(db, "admin_users", usuarioRuta.adminUid, "rutas", usuarioRuta.uid, "allClients");

export const construirAllClientsPayload = (
  datosCliente,
  clienteUid,
  imageUrl,
  imageTienda,
  createdAt,
  metadata: AllClientsMetadata = {},
) => ({
  clienteUid: clienteUid ?? null,
  ...(createdAt ? { createdAt } : {}),
  cpf: datosCliente.cpf,
  nombreCliente: datosCliente.nombreCliente,
  direccionCobro: datosCliente.direccionCobro,
  telefono: datosCliente.telefono,
  direccion: datosCliente.direccion,
  descripcion: datosCliente.descripcion,
  ubicacion: datosCliente.ubicacion,
  imageUrl,
  imageTienda,
  ...(metadata.ultimoCreditoUid ? { ultimoCreditoUid: metadata.ultimoCreditoUid } : {}),
  ...(metadata.ultimaRenovacionAt ? { ultimaRenovacionAt: metadata.ultimaRenovacionAt } : {}),
});

const normalizarTexto = (value) => String(value ?? "").trim();

const esDocumentoAllClientsLegado = (docData, cliente) => {
  if (cliente?.uid && docData?.clienteUid === cliente.uid) {
    return true;
  }

  const comparableFields = [
    "cpf",
    "nombreCliente",
    "telefono",
    "direccionCobro",
    "descripcion",
  ].filter((field) => normalizarTexto(cliente?.[field]).length > 0);

  if (!comparableFields.length) {
    return false;
  }

  return comparableFields.every(
    (field) => normalizarTexto(docData?.[field]) === normalizarTexto(cliente?.[field]),
  );
};

export const resolveAllClientsDocRef = async ({ db, usuarioRuta, cliente }) => {
  if (!usuarioRuta?.adminUid || !usuarioRuta?.uid) {
    return null;
  }

  const allClientsCollectionRef = crearAllClientsCollectionRef(db, usuarioRuta);
  const perfilUid = normalizarTexto(cliente?.perfilUid);

  if (perfilUid) {
    return doc(allClientsCollectionRef, perfilUid);
  }

  if (!cliente?.uid) {
    return null;
  }

  const canonicalDocRef = doc(allClientsCollectionRef, cliente.uid);
  const canonicalSnapshot = await getDoc(canonicalDocRef);

  if (canonicalSnapshot.exists()) {
    return canonicalDocRef;
  }

  const allClientsSnapshot = await getDocs(allClientsCollectionRef);
  const legacyDoc = allClientsSnapshot.docs.find((clientDoc) =>
    esDocumentoAllClientsLegado(clientDoc.data(), cliente),
  );

  return legacyDoc?.ref ?? canonicalDocRef;
};

export const guardarClienteEnFirebase = async (
  clienteData,
  usuarioRuta,
  fechaInicialState,
  setInfoClientes,
  _infoClientes,
) => {
  try {
    const db = getFirestore();
    const clientesCollectionRef = collection(
      db,
      "admin_users",
      usuarioRuta.adminUid,
      "rutas",
      usuarioRuta.uid,
      "clientes",
    );
    const clienteDocRef = doc(clientesCollectionRef);
    const perfilUid = clienteData.perfilUid ?? clienteDocRef.id;
    const allClientsDocRef = doc(crearAllClientsCollectionRef(db, usuarioRuta), perfilUid);
    const createdAt = new Date();
    const esRenovacion = Boolean(clienteData.esRenovacion && clienteData.perfilUid);

    const docData = {
      createdAt,
      perfilUid,
      cpf: clienteData.datosCliente.cpf,
      nombreCliente: clienteData.datosCliente.nombreCliente,
      direccionCobro: clienteData.datosCliente.direccionCobro,
      telefono: clienteData.datosCliente.telefono,
      direccion: clienteData.datosCliente.direccion,
      descripcion: clienteData.datosCliente.descripcion,
      ubicacion: clienteData.datosCliente.ubicacion,
      imageUrl: clienteData.imageUrl,
      imageTienda: clienteData.imageTiendaUrl,
      valorPrestamo: clienteData.datosCliente.valorPrestamo,
      porcentajeInteres: clienteData.datosCliente.porcentajeInteres,
      cuotasPactadas: clienteData.datosCliente.cuotasPactadas,
      pagoDiario: clienteData.datosCliente.pagoDiario,
      abono: clienteData.datosCliente.abono,
      cuotasPagadas: clienteData.datosCliente.cuotasPagadas,
      formaDePago: clienteData.datosCliente.formaDePago,
      fechaActual: formatearFechaInicial(fechaInicialState),
      fechaFinal: clienteData.datosCliente.fechaFinal,
      valorPico: clienteData.datosCliente.valorPico,
      fechaUltimoAbono: clienteData.datosCliente.fechaUltimoAbono,
      totalAbono: clienteData.datosCliente.totalAbono,
      cuotasAtrasadas: clienteData.datosCliente.cuotasAtrasadas,
      actualizado: clienteData.datosCliente.actualizado,
      historialPagos: clienteData.datosCliente.historialPagos,
    };

    const docDataAllClients = construirAllClientsPayload(
      clienteData.datosCliente,
      clienteDocRef.id,
      clienteData.imageUrl,
      clienteData.imageTiendaUrl,
      clienteData.perfilCreatedAt ?? createdAt,
      {
        ultimoCreditoUid: clienteDocRef.id,
        ...(esRenovacion ? { ultimaRenovacionAt: createdAt } : {}),
      },
    );

    const batch = writeBatch(db);
    batch.set(clienteDocRef, docData);
    batch.set(allClientsDocRef, docDataAllClients, { merge: true });
    await batch.commit();

    const nuevoCliente = {
      uid: clienteDocRef.id,
      ...docData,
    };

    setInfoClientes((previousClientes) => {
      if (!previousClientes?.length) {
        return [nuevoCliente];
      }

      const clienteExistente = previousClientes.find((cliente) => cliente.uid === nuevoCliente.uid);

      if (!clienteExistente) {
        return [...previousClientes, nuevoCliente];
      }

      return previousClientes.map((cliente) =>
        cliente.uid === nuevoCliente.uid
          ? {
              ...cliente,
              ...nuevoCliente,
            }
          : cliente,
      );
    });

    return nuevoCliente;
  } catch (error) {
    console.error("Error al guardar el cliente en Firebase:", error);
    throw error;
  }
};

export const uploadImage = async ({ image, nombre }) => {
  try {
    if (image) {
      const storageRef = ref(storage, `clientes/${nombre}`);

      await uploadBytes(storageRef, image);
      const downloadURL = await getDownloadURL(storageRef);
      return downloadURL;
    }

    return null;
  } catch (error) {
    console.error("Error uploading image to Firebase Storage:", error);
    return null;
  }
};

export const uploadImageTienda = async ({ imageTienda, descripcion }) => {
  try {
    if (imageTienda) {
      const storageRef = ref(storage, `tiendas/${descripcion}`);

      await uploadBytes(storageRef, imageTienda);
      const downloadURL = await getDownloadURL(storageRef);
      return downloadURL;
    }

    return null;
  } catch (error) {
    console.error("Error uploading image to Firebase Storage:", error);
    return null;
  }
};
